import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

export const CURRENCIES = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "INR", name: "Indian Rupee" },
  { code: "BRL", name: "Brazilian Real" },
  { code: "MXN", name: "Mexican Peso" },
  { code: "ZAR", name: "South African Rand" },
  { code: "NGN", name: "Nigerian Naira" },
  { code: "SEK", name: "Swedish Krona" },
  { code: "NOK", name: "Norwegian Krone" },
  { code: "DKK", name: "Danish Krone" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "NZD", name: "New Zealand Dollar" },
  { code: "SGD", name: "Singapore Dollar" },
];

const EURO_ZONE = ["AT", "BE", "HR", "CY", "EE", "FI", "FR", "DE", "GR", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PT", "SK", "SI", "ES"];
const REGION_CURRENCY = {
  US: "USD", GB: "GBP", CA: "CAD", AU: "AUD", JP: "JPY", IN: "INR", BR: "BRL",
  MX: "MXN", ZA: "ZAR", NG: "NGN", SE: "SEK", NO: "NOK", DK: "DKK", CH: "CHF",
  NZ: "NZD", SG: "SGD",
};
EURO_ZONE.forEach((r) => { REGION_CURRENCY[r] = "EUR"; });

export function detectCurrency() {
  try {
    const region = new Intl.Locale(navigator.language || "en-US").maximize().region;
    return REGION_CURRENCY[region] || "USD";
  } catch {
    return "USD";
  }
}

const LS_KEY = "bfa_currency";
const EVT = "bfa:currency-changed";
let ratesCache = null;
let ratesPromise = null;

function loadRates() {
  if (ratesCache) return Promise.resolve(ratesCache);
  if (!ratesPromise) {
    ratesPromise = api
      .get("/billing/fx-rates")
      .then((r) => {
        ratesCache = r.data?.rates || { USD: 1 };
        return ratesCache;
      })
      .catch(() => {
        ratesPromise = null;
        return { USD: 1 };
      });
  }
  return ratesPromise;
}

// Shared display-currency hook: selection persists in localStorage and syncs
// across all mounted components. Prices remain billed in USD at checkout.
export function useCurrency() {
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || detectCurrency(); } catch { return "USD"; }
  });
  const [rates, setRates] = useState(ratesCache || { USD: 1 });

  useEffect(() => { loadRates().then(setRates); }, []);
  useEffect(() => {
    const h = (e) => setCurrencyState(e.detail);
    window.addEventListener(EVT, h);
    return () => window.removeEventListener(EVT, h);
  }, []);

  const setCurrency = useCallback((c) => {
    try { localStorage.setItem(LS_KEY, c); } catch { /* private mode */ }
    setCurrencyState(c);
    window.dispatchEvent(new CustomEvent(EVT, { detail: c }));
  }, []);

  const active = rates[currency] ? currency : "USD";
  const rate = rates[active] || 1;

  const fmt = useCallback((usd, opts = {}) => {
    const v = (Number(usd) || 0) * rate;
    let decimals = opts.decimals ?? ((Math.abs(v) >= 10 || Number.isInteger(Number(v.toFixed(2)))) ? 0 : 2);
    if (active === "JPY") decimals = 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: active,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(v);
    } catch {
      return `$${usd}`;
    }
  }, [active, rate]);

  return { currency: active, setCurrency, fmt, isUsd: active === "USD", rate };
}
